/**
 * DYNAMIC MODULE PAGE — Full CRUD page for custom (and system) modules.
 * Renders list, create, edit, and detail views dynamically based on module configuration.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, LinearProgress, Alert,
  Breadcrumbs, Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from '../components/common/PageHeader';
import DynamicFormRenderer from '../components/dynamic/DynamicFormRenderer';
import DynamicListRenderer from '../components/dynamic/DynamicListRenderer';
import DynamicDetailRenderer from '../components/dynamic/DynamicDetailRenderer';
import configService from '../services/configService';

export default function DynamicModulePage() {
  const { moduleKey, recordId, action } = useParams();
  const history = useHistory();

  const [moduleConfig, setModuleConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [currentRecord, setCurrentRecord] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Determine view mode
  const view = recordId === 'create' ? 'create'
    : action === 'edit' ? 'edit'
    : recordId ? 'detail'
    : 'list';
  const actualRecordId = recordId === 'create' ? null : recordId;

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // Load module configuration
  const loadConfig = useCallback(async () => {
    try {
      setLoadError(null);
      const r = await configService.getModuleByKey(moduleKey);
      if (r.success) setModuleConfig(r.data);
      else setLoadError('Moduli nuk u gjet');
    } catch (e) {
      setLoadError(e.message || 'Gabim gjat\u00eb ngarkimit t\u00eb modulit');
    }
  }, [moduleKey]);

  // Load records
  const loadRecords = useCallback(async () => {
    if (!moduleConfig) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await configService.getDynamicRecords(moduleKey, { page, search, sortBy, sortDir, limit: 50 });
      if (r.success) {
        setRecords(r.data.records || []);
        setTotal(r.data.total || 0);
      }
    } catch (e) {
      setLoadError(e.message);
      setRecords([]);
    }
    setLoading(false);
  }, [moduleKey, moduleConfig, page, search, sortBy, sortDir]);

  // Load single record
  const loadRecord = useCallback(async () => {
    if (!actualRecordId || !moduleConfig) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await configService.getDynamicRecord(moduleKey, actualRecordId);
      if (r.success) {
        setCurrentRecord(r.data);
        setFormValues(r.data.data || {});
      } else {
        setLoadError('Regjistrimi nuk u gjet');
      }
    } catch (e) {
      setLoadError(e.message);
    }
    setLoading(false);
  }, [moduleKey, actualRecordId, moduleConfig]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => {
    if (!moduleConfig) return;
    if (view === 'list') loadRecords();
    else if (view === 'detail' || view === 'edit') loadRecord();
    else if (view === 'create') {
      setFormValues({});
      setFormErrors({});
      setCurrentRecord(null);
      setLoading(false);
    }
  }, [view, moduleConfig, loadRecords, loadRecord]);

  // Client-side validate
  const validate = () => {
    const errors = {};
    const visKey = view === 'create' ? 'show_in_create' : 'show_in_edit';
    for (const f of (moduleConfig?.fields || [])) {
      if (f.is_required && f.is_active && f[visKey]) {
        const v = formValues[f.key];
        if (v === undefined || v === null || v === '' || v === false) {
          errors[f.key] = `${f.label} \u00ebsht\u00eb e detyrueshme`;
        }
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save record
  const handleSave = async () => {
    if (!validate()) {
      showSnack('Ju lutem plot\u00ebsoni t\u00eb gjitha fushat e detyrueshme', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (view === 'create') {
        const r = await configService.createDynamicRecord(moduleKey, { data: formValues });
        if (r.success) {
          showSnack('Regjistrimi u krijua me sukses');
          history.push(`/m/${moduleKey}`);
        }
      } else if (view === 'edit') {
        const r = await configService.updateDynamicRecord(moduleKey, actualRecordId, { data: formValues });
        if (r.success) {
          showSnack('Regjistrimi u p\u00ebrdit\u00ebsua me sukses');
          history.push(`/m/${moduleKey}/${actualRecordId}`);
        }
      }
    } catch (e) {
      // Handle backend validation errors
      if (e.message === 'Validation failed') {
        try {
          const resp = await e.response?.json();
          if (resp?.errors) { setFormErrors(resp.errors); return; }
        } catch {}
      }
      showSnack('Gabim: ' + e.message, 'error');
    }
    setSaving(false);
  };

  // Delete record
  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await configService.deleteDynamicRecord(moduleKey, deleteDialog.id);
      setDeleteDialog(null);
      showSnack('Regjistrimi u fshi me sukses');
      if (view === 'detail') history.push(`/m/${moduleKey}`);
      else loadRecords();
    } catch (e) {
      showSnack('Gabim gjat\u00eb fshirjes: ' + e.message, 'error');
    }
  };

  // Loading state for initial config
  if (!moduleConfig && !loadError) {
    return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  }

  // Error state for missing module
  if (loadError && !moduleConfig) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto' }}>
          {loadError}
        </Alert>
        <Button variant="outlined" sx={{ mt: 2, textTransform: 'none' }} onClick={() => history.push('/')}>
          Kthehu n\u00eb faqen kryesore
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <Link color="inherit" href={`/#/m/${moduleKey}`} underline="hover" sx={{ fontSize: '0.85rem' }}>
          {moduleConfig.name}
        </Link>
        {view === 'create' && <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>Krijo</Typography>}
        {view === 'edit' && <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>Ndrysho #{actualRecordId}</Typography>}
        {view === 'detail' && <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>#{actualRecordId}</Typography>}
      </Breadcrumbs>

      {/* LIST VIEW */}
      {view === 'list' && (
        <DynamicListRenderer
          moduleKey={moduleKey}
          moduleConfig={moduleConfig}
          records={records}
          total={total}
          page={page}
          loading={loading}
          error={loadError}
          sortBy={sortBy}
          sortDir={sortDir}
          onPageChange={p => { setPage(p); }}
          onSearch={s => { setSearch(s); setPage(1); }}
          onSort={(field, dir) => { setSortBy(field); setSortDir(dir); }}
          onCreate={() => history.push(`/m/${moduleKey}/create`)}
          onView={(rec) => history.push(`/m/${moduleKey}/${rec.id}`)}
          onEdit={(rec) => history.push(`/m/${moduleKey}/${rec.id}/edit`)}
          onDelete={(rec) => setDeleteDialog(rec)}
          title={moduleConfig.name}
        />
      )}

      {/* CREATE VIEW */}
      {view === 'create' && (
        <>
          <PageHeader
            title={`${moduleConfig.name} \u2014 Krijo`}
            actions={[
              <Button key="back" variant="outlined" size="small" startIcon={<ArrowBackIcon />}
                onClick={() => history.push(`/m/${moduleKey}`)} sx={{ textTransform: 'none' }}>
                Kthehu
              </Button>,
              <Button key="save" variant="contained" size="small" startIcon={<SaveIcon />}
                onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
                {saving ? 'Duke ruajtur...' : 'Ruaj'}
              </Button>,
            ]}
          />
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mt: 2 }}>
            <DynamicFormRenderer
              fields={moduleConfig.fields}
              sections={moduleConfig.sections}
              formType="create"
              values={formValues}
              onChange={setFormValues}
              errors={formErrors}
            />
          </Paper>
        </>
      )}

      {/* EDIT VIEW */}
      {view === 'edit' && (
        <>
          <PageHeader
            title={`${moduleConfig.name} \u2014 Ndrysho`}
            subtitle={`Regjistrimi #${actualRecordId}`}
            actions={[
              <Button key="back" variant="outlined" size="small" startIcon={<ArrowBackIcon />}
                onClick={() => history.push(`/m/${moduleKey}/${actualRecordId}`)} sx={{ textTransform: 'none' }}>
                Kthehu
              </Button>,
              <Button key="save" variant="contained" size="small" startIcon={<SaveIcon />}
                onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
                {saving ? 'Duke ruajtur...' : 'Ruaj ndryshimet'}
              </Button>,
            ]}
          />
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mt: 2 }}>
            {loading ? <LinearProgress /> : (
              <DynamicFormRenderer
                fields={moduleConfig.fields}
                sections={moduleConfig.sections}
                formType="edit"
                values={formValues}
                onChange={setFormValues}
                errors={formErrors}
              />
            )}
          </Paper>
        </>
      )}

      {/* DETAIL VIEW */}
      {view === 'detail' && (
        <>
          <PageHeader
            title={moduleConfig.name}
            subtitle={currentRecord ? `Regjistrimi #${currentRecord.id}` : ''}
            actions={[
              <Button key="back" variant="outlined" size="small" startIcon={<ArrowBackIcon />}
                onClick={() => history.push(`/m/${moduleKey}`)} sx={{ textTransform: 'none' }}>
                Kthehu
              </Button>,
              <Button key="delete" variant="outlined" size="small" color="error" startIcon={<DeleteIcon />}
                onClick={() => currentRecord && setDeleteDialog(currentRecord)} sx={{ textTransform: 'none' }}>
                Fshi
              </Button>,
              <Button key="edit" variant="contained" size="small" startIcon={<EditIcon />}
                onClick={() => history.push(`/m/${moduleKey}/${actualRecordId}/edit`)} sx={{ textTransform: 'none' }}>
                Ndrysho
              </Button>,
            ]}
          />
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mt: 2 }}>
            {loading ? <LinearProgress /> : loadError ? (
              <Alert severity="error">{loadError}</Alert>
            ) : (
              <DynamicDetailRenderer
                fields={moduleConfig.fields}
                sections={moduleConfig.sections}
                values={formValues}
                record={currentRecord}
                moduleKey={moduleKey}
              />
            )}
          </Paper>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Konfirmo fshirjen</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Jeni t\u00eb sigurt q\u00eb doni t\u00eb fshini k\u00ebt\u00eb regjistrim?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Ky veprim nuk mund t\u00eb kthehet mbrapsht.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(null)} sx={{ textTransform: 'none' }}>Anulo</Button>
          <Button color="error" variant="contained" onClick={handleDelete} sx={{ textTransform: 'none' }}>
            Fshi p\u00ebrfundimisht
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
